<template>
    <div v-if="props.currentSubscription" class="addon-form">
        <div class="addon-form__addons">
            <paid-addon
                v-model="form.users"
                :default="props.currentSubscription.plan.winckPlanAddon?.users ?? 0"
                :price="AdditionalCostsCalculator.winckAddonCosts.users"
                name="Gebruikers"
                tooltip="€2,50 per gebruiker per maand voor alle extra gebruikers bovenop je basisabonnement."
            />

            <free-addon
                name="Vestigingen"
                tooltip="Zoveel vestigingen als je wilt, altijd gratis inbegrepen."
            />

            <paid-addon
                v-model="form.brands"
                :default="props.currentSubscription.plan.winckPlanAddon?.brands ?? 0"
                :price="AdditionalCostsCalculator.winckAddonCosts.brands"
                name="Merken"
                tooltip="Heb jij een extra merk? Geen probleem, voeg hem hier toe. 75 euro per extra merk per maand."
            />

            <paid-addon
                v-if="(props.currentSubscription.plan.winckPlanAddon?.contacts ?? null) !== null"
                v-model="form.contacts"
                :default="props.currentSubscription.plan.winckPlanAddon?.contacts ?? 0"
                :increment="AdditionalCostsCalculator.contactIncrement"
                :price="AdditionalCostsCalculator.winckAddonCosts.contacts"
                name="Contacten"
                tooltip="Heb jij er meer nodig? €25 per 2.500 extra contacten per maand."
            />

            <free-addon
                v-else
                name="Contacten"
                tooltip="Onbeperkt contacten voor platina abonnees."
            />

            <free-addon
                name="Standaard flows"
                tooltip="Gratis en onbeperkt, altijd alle standaard flows volledig inbegrepen."
            />

            <paid-addon
                v-model="form.premiumFlows"
                :default="props.currentSubscription.plan.winckPlanAddon?.premiumFlows ?? 0"
                :price="AdditionalCostsCalculator.winckAddonCosts.premiumFlows"
                name="Premium flows"
                tooltip="Een premium flow voeg jij toe voor 30 euro per flow per maand."
            />

            <paid-addon
                v-model="form.aiEmployees"
                :default="props.currentSubscription.plan.winckPlanAddon?.aiEmployees ?? 0"
                :price="AdditionalCostsCalculator.winckAddonCosts.aiEmployees"
                name="A.I. medewerkers"
                tooltip="Een krachtige A.I. medewerker voor 99 euro per A.I. per maand."
            />
        </div>

        <div class="addon-form__footer">
            <flex-container
                :gap="Size.sm"
                align-items="center"
                justify-content="flex-end"
            >
                <text-decorator weight="bold">
                    Additionele kosten
                </text-decorator>
                <text-decorator weight="bold" :size="FontSize.xl">
                    {{ formatMoney(props.additionalCosts) }}
                </text-decorator>
            </flex-container>

            <flex-container justify-content="flex-end" align-items="center">
                <slot name="actions" />
            </flex-container>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import FreeAddon from '@/subscription/subscription/components/FreeAddon.vue';
    import PaidAddon from '@/subscription/subscription/components/PaidAddon.vue';
    import { FontSize, Size } from '@keyvue/one-ui';
    import { formatMoney } from '@/shared/utils/money';
    import { Subscription, WinckSubscriptionAddon } from '@keyvue/one-api-client';
    import AdditionalCostsCalculator from '@/subscription/subscription/utils/AdditionalCostsCalculator';

    export interface Props {
        currentSubscription: Subscription
        loadedAdditionalCosts: number
        additionalCosts: number
    }

    export interface ExposedWinckAddonForm {
        form: WinckSubscriptionAddon,
        setForm: (values: Subscription) => void,
    }

    const props = defineProps<Props>();

    const form = ref<WinckSubscriptionAddon>(WinckSubscriptionAddon.fromObject({
        users: 0,
        contacts: 0,
        brands: 0,
        premiumFlows: 0,
        aiEmployees: 0,
    }) as WinckSubscriptionAddon);

    function setForm(subscription: Subscription) {
        if (!subscription.winckSubscriptionAddon) {
            return;
        }

        form.value.users = subscription.winckSubscriptionAddon.users;
        form.value.contacts = subscription.winckSubscriptionAddon.contacts;
        form.value.brands = subscription.winckSubscriptionAddon.brands;
        form.value.premiumFlows = subscription.winckSubscriptionAddon.premiumFlows;
        form.value.aiEmployees = subscription.winckSubscriptionAddon.aiEmployees;
    }

    defineExpose({
        form,
        setForm,
    });
</script>

<style scoped lang="scss">
    .addon-form {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        height: 100%;

        &__addons {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            padding-top: size(md);
            display: grid;
            gap: size(md);
            grid-template-columns: 1fr 1fr 1fr 1fr;
            grid-auto-rows: 1fr;
        }

        &__footer {
            display: flex;
            flex-wrap: wrap;
            padding-top: size(xl);
            gap: size(md);
            width: 100%;
            align-self: flex-end;
        }
    }
</style>
